import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import { useDispatch } from "react-redux";
import {
  LuIndianRupee,
  LuUsers,
  LuCalendar,
  LuInfo,
  LuTrendingUp,
  LuWallet,
  LuCake,
} from "react-icons/lu";
import { getRmDashboardData } from "../../redux/dashboard/dashboardSlice";
import AppLoader from "../../components/AppLoader/AppLoader";
import { formatAmount } from "../../utils";
import { OFF_WHITE } from "../../constants/layout";

const BLACK = "#000000";
const DARK_NAVY = "#051525";
const DARK_NAVY_LIGHT = "#4a6278";
const ICON_BLUE = "#1E3A5F";
const GRAPH_BLUE = "#1E3A5F";
const CARD_SHADOW = "0 2px 16px rgba(0, 0, 0, 0.06)";
const PROFILE_FONT = '"Inter", sans-serif';
const MIDDLE_ROW_CARD_HEIGHT = 400;
const HOLDING_CHART_MIN_HEIGHT = 360;

type MetricType = "aum" | "investors" | "brokerage";

const HOLDING_COLORS: Record<string, string> = {
  "Mutual Fund": "#1E3A5F",
  AIF: "#E07A2F",
  PMS: "#2E9B6A",
  "Fixed Deposit": "#3B82F6",
  Bond: "#8B5CF6",
  Stock: "#EC4899",
  "Private Stock": "#F59E0B",
  SIF: "#14B8A6",
  "International Equity": "#6366F1",
  Insurance: "#0EA5E9",
  Other: "#64748B",
  REIT: "#84CC16",
};

const cardSx = {
  bgcolor: "#ffffff",
  borderRadius: "16px",
  boxShadow: CARD_SHADOW,
  border: "1px solid rgba(0,0,0,0.04)",
  p: { xs: 2, md: 2.5 },
  height: "100%",
};

const formatAmountDisplay = (amount: number) => {
  if (amount >= 1e7) {
    return `₹${(amount / 1e7).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    })} Cr`;
  }
  if (amount >= 1e5) {
    return `₹${(amount / 1e5).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    })} L`;
  }
  return formatAmount(amount);
};

type HoldingItem = { holdingType: string; amount: number };

type RmDashboardData = {
  investments?: { totalInvestment?: number; data?: HoldingItem[] };
  investorsCount?: {
    investors?: number;
    newInvestors?: number;
    monthlyInvestors?: { count: number; month: string }[];
  };
  activeSips?: number;
  upcomingSips?: { sipDate: string; userName: string; schemeName: string }[];
  upcomingFdMaturities?: {
    userName: string;
    schemeName: string;
    maturityDate: string;
    maturityAmount: number;
  }[];
  upcomingUserBirthdays?: { userName: string; dateOfBirth: string }[];
  topSchemes?: {
    mf?: { returns: number | string; category: string; schemeName: string }[];
    aif?: { returns: number | string; category: string; schemeName: string }[];
    pms?: { returns: number | string; category: string; schemeName: string }[];
  };
  partnerData?: { amountToPartner?: number; brokerageSummary?: HoldingItem[] };
};

const StatCard = ({
  label,
  value,
  subtitle,
  icon: Icon,
  isCurrency = false,
  infoTooltip,
  isActive = false,
  onClick,
}: {
  label: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  isCurrency?: boolean;
  infoTooltip?: string;
  isActive?: boolean;
  onClick?: () => void;
}) => (
  <Box
    onClick={onClick}
    sx={{
      ...cardSx,
      cursor: onClick ? "pointer" : "default",
      border: isActive ? `2px solid ${ICON_BLUE}` : "1px solid rgba(0,0,0,0.04)",
      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
      "&:hover": onClick ? { boxShadow: "0 4px 20px rgba(0,0,0,0.08)" } : {},
    }}
  >
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, mb: 1 }}>
      <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, fontWeight: 500, color: DARK_NAVY_LIGHT }}>
        {label}
      </Typography>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "12px",
          bgcolor: isActive ? ICON_BLUE : "rgba(30, 58, 95, 0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={22} color={isActive ? "#ffffff" : ICON_BLUE} strokeWidth={2} />
      </Box>
    </Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Typography
        sx={{
          fontFamily: PROFILE_FONT,
          fontSize: isCurrency ? { xs: 18, sm: 20, md: 22 } : { xs: 22, md: 26 },
          fontWeight: 700,
          color: BLACK,
          lineHeight: 1.25,
          whiteSpace: isCurrency ? "nowrap" : "normal",
        }}
      >
        {value}
      </Typography>
      {infoTooltip ? (
        <Tooltip
          title={<Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, p: 0.5 }}>{infoTooltip}</Typography>}
          arrow
          placement="top"
        >
          <IconButton
            size="small"
            aria-label="View full amount"
            onClick={(e) => e.stopPropagation()}
            sx={{ p: 0.25, color: ICON_BLUE, "&:hover": { bgcolor: "rgba(30, 58, 95, 0.08)" } }}
          >
            <LuInfo size={18} strokeWidth={2} />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
    {subtitle ? (
      <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, color: "#08991F", fontWeight: 500, mt: 0.75 }}>
        {subtitle}
      </Typography>
    ) : null}
  </Box>
);

const HoldingsTableColumn = ({
  items,
  totalInvestment,
}: {
  items: HoldingItem[];
  totalInvestment: number;
}) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Grid container sx={{ borderBottom: "1px solid #E8E8E8", pb: 1, mb: 0.5 }}>
      <Grid item xs={5}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: DARK_NAVY_LIGHT, textTransform: "uppercase" }}>
          Holding Type
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: DARK_NAVY_LIGHT, textTransform: "uppercase", textAlign: "right" }}>
          Amount (₹)
        </Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: DARK_NAVY_LIGHT, textTransform: "uppercase", textAlign: "right" }}>
          %
        </Typography>
      </Grid>
    </Grid>
    {items.map((item) => {
      const pct = totalInvestment > 0 ? ((item.amount / totalInvestment) * 100).toFixed(2) : "0.00";
      const color = HOLDING_COLORS[item.holdingType] || ICON_BLUE;
      return (
        <Grid container key={item.holdingType} sx={{ py: 0.75, borderBottom: "1px solid #F5F5F5", alignItems: "center" }}>
          <Grid item xs={5}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
              <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, fontWeight: 600, color: DARK_NAVY, lineHeight: 1.3 }}>
                {item.holdingType}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 11, textAlign: "right", color: BLACK, wordBreak: "break-word" }}>
              {formatAmount(item.amount)}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 11, textAlign: "right", color: DARK_NAVY_LIGHT }}>
              {pct}%
            </Typography>
          </Grid>
        </Grid>
      );
    })}
  </Box>
);

const InvestmentChart = ({
  holdings,
  totalInvestment,
  pieData,
}: {
  holdings: HoldingItem[];
  totalInvestment: number;
  pieData: { id: string; label: string; value: number; color: string }[];
}) => {
  const useTwoColumns = holdings.length > 6;
  const splitAt = Math.ceil(holdings.length / 2);
  const leftHoldings = useTwoColumns ? holdings.slice(0, splitAt) : holdings;
  const rightHoldings = useTwoColumns ? holdings.slice(splitAt) : [];

  return (
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", md: "row" },
      alignItems: { xs: "center", md: "flex-start" },
      gap: { xs: 2, md: 3 },
      width: "100%",
    }}
  >
    <Box sx={{ position: "relative", width: 260, height: 260, flexShrink: 0 }}>
      <PieChart
        series={[
          {
            data: pieData,
            innerRadius: 76,
            outerRadius: 114,
            paddingAngle: 1.5,
            cornerRadius: 3,
            cx: 130,
            cy: 130,
            arcLabel: () => "",
          },
        ]}
        width={260}
        height={260}
        margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
        sx={{
          "& .MuiChartsLegend-root": { display: "none" },
          "& .MuiPieArcLabel-root": { display: "none" },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          textAlign: "center",
          width: 120,
          pointerEvents: "none",
        }}
      >
        <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, color: DARK_NAVY_LIGHT, mb: 0.5 }}>
          Total Investment
        </Typography>
        <Typography
          sx={{
            fontFamily: PROFILE_FONT,
            fontSize: 12,
            fontWeight: 700,
            color: BLACK,
            lineHeight: 1.35,
          }}
        >
          {formatAmountDisplay(totalInvestment)}
        </Typography>
      </Box>
    </Box>

    <Box
      sx={{
        flex: 1,
        width: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: 2, md: 3 },
      }}
    >
      <HoldingsTableColumn items={leftHoldings} totalInvestment={totalInvestment} />
      {useTwoColumns ? (
        <HoldingsTableColumn items={rightHoldings} totalInvestment={totalInvestment} />
      ) : null}
    </Box>
  </Box>
  );
};

const InvestorBarChart = ({ data }: { data: { month: string; count: number }[] }) => (
  <Box sx={{ height: 240, width: "100%" }}>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" vertical={false} />
        <XAxis dataKey="month" tick={{ fontSize: 12, fill: DARK_NAVY_LIGHT }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 12, fill: DARK_NAVY_LIGHT }} axisLine={false} tickLine={false} allowDecimals={false} />
        <RechartsTooltip contentStyle={{ fontFamily: PROFILE_FONT, borderRadius: 8, border: "1px solid #E8E8E8" }} />
        <Bar dataKey="count" fill={GRAPH_BLUE} radius={[6, 6, 0, 0]} barSize={36} />
      </BarChart>
    </ResponsiveContainer>
  </Box>
);

const getMetricTitle = (metric: MetricType) => {
  switch (metric) {
    case "aum":
      return "Investment by Holding Type";
    case "investors":
      return "New Investors Trend";
    case "brokerage":
      return "Brokerage Analysis";
    default:
      return "";
  }
};

const ListRow = ({ primary, secondary, meta }: { primary: string; secondary?: string; meta?: string }) => (
  <Box sx={{ py: 1.5, borderBottom: "1px solid #F0F0F0", "&:last-child": { borderBottom: "none" } }}>
    <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, fontWeight: 600, color: DARK_NAVY, lineHeight: 1.4 }}>{primary}</Typography>
    {secondary ? <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, color: DARK_NAVY_LIGHT, mt: 0.25 }}>{secondary}</Typography> : null}
    {meta ? <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, fontWeight: 600, color: ICON_BLUE, mt: 0.5 }}>{meta}</Typography> : null}
  </Box>
);

const UpcomingSipsCard = ({
  sips,
}: {
  sips: { sipDate: string; userName: string; schemeName: string }[];
}) => (
  <Box
    sx={{
      ...cardSx,
      p: { xs: 2, md: 3 },
      display: "flex",
      flexDirection: "column",
      height: MIDDLE_ROW_CARD_HEIGHT,
      maxHeight: MIDDLE_ROW_CARD_HEIGHT,
      overflow: "hidden",
    }}
  >
    <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 18, fontWeight: 700, color: DARK_NAVY, mb: 1.5, flexShrink: 0 }}>
      Upcoming SIPs
    </Typography>
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        pr: 0.5,
        "&::-webkit-scrollbar": { width: 4 },
        "&::-webkit-scrollbar-thumb": { bgcolor: "#D0D0D0", borderRadius: 4 },
      }}
    >
      {sips.length > 0 ? (
        sips.map((sip, idx) => (
          <Box
            key={`${sip.userName}-${sip.sipDate}-${idx}`}
            sx={{
              py: 1.25,
              borderBottom: idx < sips.length - 1 ? "1px solid #F0F0F0" : "none",
            }}
          >
            <Typography
              sx={{
                fontFamily: PROFILE_FONT,
                fontSize: 13,
                fontWeight: 600,
                color: DARK_NAVY,
                lineHeight: 1.4,
                textTransform: "uppercase",
              }}
            >
              {sip.userName}
            </Typography>
            <Typography
              sx={{
                fontFamily: PROFILE_FONT,
                fontSize: 12,
                color: DARK_NAVY_LIGHT,
                mt: 0.5,
                lineHeight: 1.4,
              }}
            >
              {sip.schemeName}
            </Typography>
            <Typography
              sx={{
                fontFamily: PROFILE_FONT,
                fontSize: 12,
                fontWeight: 600,
                color: ICON_BLUE,
                mt: 0.5,
              }}
            >
              {sip.sipDate}
            </Typography>
          </Box>
        ))
      ) : (
        <Typography sx={{ fontFamily: PROFILE_FONT, color: DARK_NAVY_LIGHT, fontSize: 13, py: 2 }}>
          No upcoming SIPs
        </Typography>
      )}
    </Box>
  </Box>
);

const Dashboard = () => {
  const dispatch = useDispatch<any>();
  const userType = localStorage.getItem("userType");
  const [data, setData] = useState<RmDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [schemeTab, setSchemeTab] = useState(0);
  const [selectedMetric, setSelectedMetric] = useState<MetricType>("aum");

  useEffect(() => {
    const fetchData = async () => {
      if (!userType) return;
      setLoading(true);
      try {
        const res = await dispatch(
          getRmDashboardData({ forRm: userType === "2" ? 1 : userType === "6" ? 0 : null })
        );
        if (!res?.error) setData(res?.payload?.data ?? null);
      } catch (e) {
        console.error("Error fetching dashboard data:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dispatch, userType]);

  const holdings = useMemo(
    () => (data?.investments?.data ?? []).filter((item) => item.amount > 0),
    [data?.investments?.data]
  );
  const totalInvestment = data?.investments?.totalInvestment ?? 0;

  const pieData = useMemo(
    () =>
      holdings.map((item) => ({
        id: item.holdingType,
        label: item.holdingType,
        value: item.amount,
        color: HOLDING_COLORS[item.holdingType] || ICON_BLUE,
      })),
    [holdings]
  );

  const investorChartData = useMemo(() => {
    const months = data?.investorsCount?.monthlyInvestors ?? [];
    return [...months].reverse();
  }, [data]);

  const schemeTabKey = (["mf", "aif", "pms"] as const)[schemeTab];
  const schemeList = data?.topSchemes?.[schemeTabKey] ?? [];

  const handleMetricSelect = (metric: MetricType) => {
    setSelectedMetric(metric);
  };

  const renderMetricContent = () => {
    if (selectedMetric === "aum") {
      return pieData.length > 0 ? (
        <InvestmentChart holdings={holdings} totalInvestment={totalInvestment} pieData={pieData} />
      ) : (
        <Typography sx={{ color: DARK_NAVY_LIGHT, py: 4, textAlign: "center" }}>No holding data</Typography>
      );
    }
    if (selectedMetric === "investors") {
      return investorChartData.length > 0 ? (
        <InvestorBarChart data={investorChartData} />
      ) : (
        <Typography sx={{ color: DARK_NAVY_LIGHT, py: 4, textAlign: "center" }}>No investor trend data</Typography>
      );
    }
    if (selectedMetric === "brokerage") {
      const brokerageData = data?.partnerData?.brokerageSummary ?? [];
      return brokerageData.length > 0 ? (
        <InvestorBarChart
          data={brokerageData.map((b) => ({ month: b.holdingType, count: b.amount }))}
        />
      ) : (
        <Typography sx={{ color: DARK_NAVY_LIGHT, py: 4, textAlign: "center" }}>No brokerage data</Typography>
      );
    }
    return null;
  };

  const dashboardTitle = userType === "6" ? "Partner Dashboard" : "RM Dashboard";

  return (
    <Box sx={{ minHeight: "100%", bgcolor: OFF_WHITE, px: { xs: 3, sm: 5, md: 6, lg: 8 }, py: { xs: 3, md: 4 }, fontFamily: PROFILE_FONT }}>
      <AppLoader loading={loading} />
      <Box sx={{ maxWidth: 1280, mx: "auto" }}>
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: { xs: 24, md: 28 }, fontWeight: 700, color: DARK_NAVY, mb: 0.5 }}>{dashboardTitle}</Typography>
          <Typography sx={{ fontSize: 14, color: DARK_NAVY_LIGHT }}>Welcome back! Here&apos;s your business overview</Typography>
        </Box>

        {!loading && !data ? (
          <Typography sx={{ color: DARK_NAVY_LIGHT, py: 6, textAlign: "center" }}>No dashboard data available</Typography>
        ) : !loading && data ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard
                  label="Total Investment"
                  value={formatAmountDisplay(totalInvestment)}
                  infoTooltip={formatAmount(totalInvestment)}
                  icon={LuIndianRupee}
                  isCurrency
                  subtitle="Across all asset classes"
                  isActive={selectedMetric === "aum"}
                  onClick={() => handleMetricSelect("aum")}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard
                  label="Investors"
                  value={data.investorsCount?.investors ?? 0}
                  icon={LuUsers}
                  subtitle={data.investorsCount?.newInvestors ? `${data.investorsCount.newInvestors} New this month` : undefined}
                  isActive={selectedMetric === "investors"}
                  onClick={() => handleMetricSelect("investors")}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard
                  label="Active SIPs"
                  value={data.activeSips ?? 0}
                  icon={LuTrendingUp}
                  subtitle="Active SIP today"
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard
                  label="Upcoming SIPs"
                  value={data.upcomingSips?.length ?? 0}
                  icon={LuCalendar}
                  subtitle="Scheduled instalments"
                />
              </Grid>
              {userType === "6" && data.partnerData?.amountToPartner != null ? (
                <Grid item xs={12} sm={6} lg={3}>
                  <StatCard
                    label="Brokerage Earned"
                    value={formatAmountDisplay(data.partnerData.amountToPartner)}
                    infoTooltip={formatAmount(data.partnerData.amountToPartner)}
                    icon={LuWallet}
                    isCurrency
                    isActive={selectedMetric === "brokerage"}
                    onClick={() => handleMetricSelect("brokerage")}
                  />
                </Grid>
              ) : null}
            </Grid>

            <Grid container spacing={2.5} alignItems="flex-start">
              <Grid item xs={12} lg={8}>
                <Box
                  sx={{
                    ...cardSx,
                    p: { xs: 2, md: 3 },
                    minHeight: selectedMetric === "aum" ? HOLDING_CHART_MIN_HEIGHT : MIDDLE_ROW_CARD_HEIGHT,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 18, fontWeight: 700, color: DARK_NAVY, mb: 0.5, flexShrink: 0 }}>
                    {getMetricTitle(selectedMetric)}
                  </Typography>
                  <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, color: DARK_NAVY_LIGHT, mb: 1.5, flexShrink: 0 }}>
                    {selectedMetric === "aum" && "Distribution across products"}
                    {selectedMetric === "investors" && "Last 6 months"}
                    {selectedMetric === "brokerage" && "Partner brokerage breakdown"}
                  </Typography>
                  <Box sx={{ flex: 1, width: "100%", minHeight: selectedMetric === "aum" ? 280 : 240 }}>
                    {renderMetricContent()}
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} lg={4}>
                <UpcomingSipsCard sips={data.upcomingSips ?? []} />
              </Grid>
            </Grid>

            <Grid container spacing={2.5}>
              <Grid item xs={12} lg={4}>
                <Box sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
                  <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 18, fontWeight: 700, color: DARK_NAVY, mb: 2 }}>Top Schemes</Typography>
                  <Tabs
                    value={schemeTab}
                    onChange={(_, v) => setSchemeTab(v)}
                    sx={{
                      minHeight: 36,
                      mb: 2,
                      "& .MuiTab-root": { fontFamily: PROFILE_FONT, fontSize: 13, fontWeight: 500, textTransform: "none", minHeight: 36, color: DARK_NAVY_LIGHT, "&.Mui-selected": { color: DARK_NAVY, fontWeight: 600 } },
                      "& .MuiTabs-indicator": { bgcolor: ICON_BLUE },
                    }}
                  >
                    <Tab label="MF" />
                    <Tab label="AIF" />
                    <Tab label="PMS" />
                  </Tabs>
                  {schemeList.length > 0 ? (
                    schemeList.map((scheme, idx) => (
                      <Box key={`${scheme.schemeName}-${idx}`} sx={{ py: 1.5, borderBottom: idx < schemeList.length - 1 ? "1px solid #F0F0F0" : "none" }}>
                        <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, fontWeight: 600, color: DARK_NAVY, lineHeight: 1.4 }}>{scheme.schemeName}</Typography>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                          <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, color: DARK_NAVY_LIGHT }}>{scheme.category}</Typography>
                          <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, fontWeight: 600, color: "#08991F" }}>
                            {scheme.returns != null && scheme.returns !== "" ? `${scheme.returns}%` : "-"}
                          </Typography>
                        </Box>
                      </Box>
                    ))
                  ) : (
                    <Typography sx={{ fontFamily: PROFILE_FONT, color: DARK_NAVY_LIGHT, fontSize: 13 }}>No schemes available</Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} lg={4}>
                <Box sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
                  <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 18, fontWeight: 700, color: DARK_NAVY, mb: 2 }}>Upcoming FD Maturities</Typography>
                  {(data.upcomingFdMaturities ?? []).length > 0 ? (
                    data.upcomingFdMaturities!.map((item, idx) => (
                      <ListRow key={`${item.userName}-${idx}`} primary={item.userName} secondary={item.schemeName} meta={`${item.maturityDate} · ${formatAmount(item.maturityAmount)}`} />
                    ))
                  ) : (
                    <Typography sx={{ fontFamily: PROFILE_FONT, color: DARK_NAVY_LIGHT, fontSize: 13 }}>No upcoming maturities</Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} lg={4}>
                <Box sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <LuCake size={18} color={ICON_BLUE} />
                    <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 18, fontWeight: 700, color: DARK_NAVY }}>Upcoming Birthdays</Typography>
                  </Box>
                  {(data.upcomingUserBirthdays ?? []).length > 0 ? (
                    data.upcomingUserBirthdays!.map((item, idx) => (
                      <ListRow key={`${item.userName}-${idx}`} primary={item.userName} meta={item.dateOfBirth} />
                    ))
                  ) : (
                    <Typography sx={{ fontFamily: PROFILE_FONT, color: DARK_NAVY_LIGHT, fontSize: 13 }}>No upcoming birthdays</Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default Dashboard;
